/** useTestCases — load and mutate an agent's evaluation cases.
 *
 * Mirrors draft-preview hooks: direct entity-API calls, refetch after each
 * mutation (test sets are small, so refetch beats optimistic bookkeeping).
 */

import { useCallback, useEffect, useState } from 'react';
import type { EvalTestCase, EvalTestCaseInput } from '@/entities/eval';
import { getEvalApi } from './evalClient';

export interface UseTestCasesResult {
  testCases: EvalTestCase[];
  loading: boolean;
  error: string | null;
  reload: () => void;
  create: (input: EvalTestCaseInput) => Promise<void>;
  update: (id: string, input: EvalTestCaseInput) => Promise<void>;
  remove: (id: string) => Promise<void>;
}

export function useTestCases(agentId: string): UseTestCasesResult {
  const [testCases, setTestCases] = useState<EvalTestCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!agentId) return;
    setLoading(true);
    setError(null);
    try {
      setTestCases(await getEvalApi().listTestCases(agentId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load test cases');
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const create = useCallback(
    async (input: EvalTestCaseInput) => {
      await getEvalApi().createTestCase(agentId, input);
      await reload();
    },
    [agentId, reload],
  );

  const update = useCallback(
    async (id: string, input: EvalTestCaseInput) => {
      await getEvalApi().updateTestCase(agentId, id, input);
      await reload();
    },
    [agentId, reload],
  );

  const remove = useCallback(
    async (id: string) => {
      await getEvalApi().deleteTestCase(agentId, id);
      await reload();
    },
    [agentId, reload],
  );

  return { testCases, loading, error, reload, create, update, remove };
}
